import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { ApiError, getUserDetails, type UserDetailsResponse } from "../../../api";
import {
  clearCompanionSession,
  getCompanionSession,
  getUserProfile,
  setUserProfile,
} from "../../../auth/session";
import {
  getDashboardBillingPath,
  getDashboardCalendarPath,
  getDashboardLoginPath,
  getDashboardOverviewPath,
  getDashboardUsagePath,
} from "../../../utils/dashboardPaths";
import "./SidePanel.scss";

interface ProfileState {
  loading: boolean;
  errorMessage: string | null;
  response: UserDetailsResponse | null;
}

export default function SidePanel(): React.JSX.Element {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<ProfileState>({
    loading: true,
    errorMessage: null,
    response: null,
  });

  useEffect(() => {
    const loadProfile = async (): Promise<void> => {
      setProfile({ loading: true, errorMessage: null, response: null });

      const cached = getUserProfile();
      // Cache-first: if we already have the key fields, avoid calling `/v1/public/user/details`.
      if (cached?.name && cached?.email_id) {
        setProfile({
          loading: false,
          errorMessage: null,
          response: {
            status: "SUCCESS",
            code: 200,
            user_name: cached.name,
            pfp: cached.pfp,
            email_id: cached.email_id,
            agent_email: cached.agent_email,
            agent_name: cached.agent_name,
          },
        });
        return;
      }

      const session = getCompanionSession();
      if (!session) {
        setProfile({
          loading: false,
          errorMessage: "Missing local session. Please login again.",
          response: null,
        });
        return;
      }

      try {
        const resp = await getUserDetails({ xSid: session.sid, xEmail: session.email });
        if (resp.status !== "SUCCESS") {
          setProfile({
            loading: false,
            errorMessage: resp.error ?? "Failed to load user details.",
            response: resp,
          });
          return;
        }

        // Update the cached profile so subsequent dashboard loads avoid extra calls.
        setUserProfile({
          agent_email: resp.agent_email,
          agent_name: resp.agent_name,
          name: resp.user_name,
          pfp: resp.pfp,
          email_id: resp.email_id,
        });
        setProfile({ loading: false, errorMessage: null, response: resp });
      } catch (error: unknown) {
        const message =
          error instanceof ApiError
            ? `${error.message} (${error.status})`
            : error instanceof Error
              ? error.message
              : "Unknown error";
        setProfile({ loading: false, errorMessage: message, response: null });
      }
    };

    void loadProfile();
  }, []);

  const userName = profile.response?.user_name ?? "";
  const emailId = profile.response?.email_id ?? "";
  const pfp = profile.response?.pfp ?? "";

  // Until billing plan is wired into `/v1/public/user/details`, keep this fixed.
  const planLabel = "Pro";

  return (
    <aside className="side-panel" aria-label="Dashboard navigation">
      <div className="side-panel-profile">
        <div className="side-panel-profile-top">
          <div className="side-panel-profile-avatar">
            {pfp ? (
              <img
                className="side-panel-profile-avatar-img"
                src={pfp}
                alt={userName ? `${userName} avatar` : "User avatar"}
              />
            ) : (
              <div className="side-panel-profile-avatar-fallback" aria-hidden="true">
                {userName ? userName.slice(0, 1).toUpperCase() : "?"}
              </div>
            )}
          </div>

          <div className="side-panel-profile-meta">
            <div className="side-panel-profile-name">
              {profile.loading ? "Loading…" : userName || "—"}
            </div>
            <div className="side-panel-plan-pill side-panel-plan-pill--pro">{planLabel}</div>
            {profile.errorMessage ? (
              <div className="side-panel-profile-error" role="status">
                {profile.errorMessage}
              </div>
            ) : null}
          </div>
        </div>
        
        <div className="side-panel-profile-email">
          {profile.loading ? " " : emailId || "—"}
        </div>
      </div>

      <nav className="side-panel-nav" aria-label="Dashboard sections">
        <div className="side-panel-nav-group">
          <NavLink
            to={getDashboardOverviewPath()}
            end
            className={({ isActive }) => (isActive ? "side-panel-link active" : "side-panel-link")}
          >
            Overview
          </NavLink>
          <NavLink
            to={getDashboardCalendarPath()}
            className={({ isActive }) => (isActive ? "side-panel-link active" : "side-panel-link")}
          >
            Link calendar
          </NavLink>
          <NavLink
            to={getDashboardUsagePath()}
            className={({ isActive }) => (isActive ? "side-panel-link active" : "side-panel-link")}
          >
            Usage
          </NavLink>
        </div>

        <div className="side-panel-divider" role="separator" />

        {/* <div className="side-panel-nav-group">
          <NavLink
            to={getDashboardBillingPath()}
            className={({ isActive }) => (isActive ? "side-panel-link active" : "side-panel-link")}
          >
            Billing
          </NavLink>
        </div> */}

        <div className="side-panel-divider" role="separator" />

        <div className="side-panel-nav-group">
          <div
            className="side-panel-link"
            onClick={() => {
              clearCompanionSession();
              navigate(getDashboardLoginPath(), { replace: true });
            }}
            >
            Logout
          </div>
        </div>

        
      </nav>
    </aside>
  );
}
